import { PenLine } from "lucide-react";

import {
  MAINTENANCE_STATUS_OPTIONS,
  MAINTENANCE_STATUS_VARIANT,
} from "../constants/maintenance.constants";
import { MaintenanceStatus, ReminderType } from "../maintenance.enums";
import { maintenanceMessages } from "../maintenance.intl";
import type { MaintenanceHistoryEntry } from "../types/maintenance.types";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import LabelValuePair from "@/components/content/LabelValuePair";
import CustomHeader from "@/components/headers";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  DATE_TIME_FORMAT,
  dateFormat,
} from "@/utils/helper-functions/date-time.helper";

interface MaintenanceSheetProps {
  isOpen: boolean;
  toggleOpen: () => void;
  entry: MaintenanceHistoryEntry | null;
  onEditEntry?: (entry: MaintenanceHistoryEntry) => void;
  onCancelEntry?: (id: string) => void;
}
const MaintenanceSheet = (props: MaintenanceSheetProps): React.JSX.Element => {
  const { isOpen, toggleOpen, entry, onEditEntry, onCancelEntry } = props;
  const t = useIntlMessages(maintenanceMessages);

  if (!entry) {
    return <></>;
  }

  const handleEdit = (): void => {
    onEditEntry?.(entry);
  };

  const handleCancel = (): void => {
    onCancelEntry?.(entry.id);
  };

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      toggleOpen();
    }
  };

  const showActions = entry.status === MaintenanceStatus.SCHEDULED;

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side='right'
        className='w-full overflow-y-auto'
        style={{ maxWidth: "712px" }}
      >
        <CustomHeader
          title={t("historyDetailsTitle")}
          backBtn={toggleOpen}
          className='mt-0'
          hide={{ functions: true, filters: true }}
          custom={{
            right: {
              node: showActions ? (
                <div className='flex items-center gap-2'>
                  <Button
                    variant='ghost'
                    className='text-red-800 text-sm'
                    onClick={handleCancel}
                  >
                    {t("abort")}
                  </Button>
                  <Button variant='outline' onClick={handleEdit}>
                    <PenLine size={16} className='mr-2' />
                    {t("edit")}
                  </Button>
                </div>
              ) : null,
            },
          }}
        />

        <div className='flex flex-col gap-6 pt-6'>
          <div>
            <p className='mb-2 text-md font-medium text-[#224092] text-sm'>
              {t("summary")}
            </p>

            <div className='flex flex-col gap-2 rounded-md border border-[#E2E8F0] p-4'>
              <LabelValuePair
                data={[
                  {
                    label: t("start"),
                    value: dateFormat(entry.startAt, DATE_TIME_FORMAT),
                  },
                  {
                    label: t("end"),
                    value: dateFormat(entry.endAt, DATE_TIME_FORMAT) ?? "-",
                  },
                ]}
                containerClassName='flex flex-col gap-2'
                labelClassName='text-xs text-gray-500 w-1/4'
                valueClassName='text-sm text-gray-800 font-medium w-3/4'
              />

              <div className='flex items-center justify-start gap-2'>
                <p className='text-gray-400 text-xs w-1/4'>
                  {t("historyStatus")}
                </p>
                <Badge variant={MAINTENANCE_STATUS_VARIANT[entry.status]}>
                  {
                    MAINTENANCE_STATUS_OPTIONS.find(
                      (option) => option.id === entry.status,
                    )?.label
                  }
                </Badge>
              </div>
            </div>
          </div>

          <div>
            <p className='mb-2 text-md font-medium text-[#224092] text-sm'>
              {t("reminder")}
            </p>

            {entry.reminderType === ReminderType.NONE && (
              <p className='italic text-gray-400 text-xs'>
                {t("noReminderSet")}
              </p>
            )}
            {entry.reminderType === ReminderType.IMMEDIATE && (
              <p className='text-gray-800 text-xs font-semibold'>
                {t("historyImmediately")}
              </p>
            )}

            {entry.reminderType === ReminderType.CUSTOM && (
              <div className='border rounded-md p-4'>
                <LabelValuePair
                  data={[
                    {
                      label: t("start"),
                      value:
                        dateFormat(entry.reminderAt, DATE_TIME_FORMAT) ?? "-",
                    },
                  ]}
                  containerClassName='flex flex-col gap-2'
                  labelClassName='text-xs text-gray-500 w-1/4'
                  valueClassName='text-sm text-gray-800 font-medium w-3/4'
                />
              </div>
            )}

            {/* // <div className='rounded-md border border-[#E2E8F0] p-4 flex w-full items-center'>
              //   {entry.reminderType === ReminderType.CUSTOM && (
              //     <p className='text-gray-400 text-xs w-[60px]'>{t("start")}</p>
              //   )}

              //   <p className='text-gray-800 text-xs font-semibold'>
              //     {entry.reminderType === ReminderType.IMMEDIATE
              //       ? t("historyImmediately")
              //       : (dateFormat(entry.reminderAt, DATE_TIME_FORMAT) ?? "-")}
              //   </p>
              // </div> */}
            {/* )} */}
          </div>

          <div>
            <p className='mb-2 text-md font-medium text-[#224092] text-sm'>
              {t("notice")}
            </p>

            {entry.notice ? (
              <div className='rounded-md border border-[#E2E8F0] p-4'>
                <p
                  className='whitespace-pre-wrap text-gray-800 text-sm'
                  title={entry.notice ?? undefined}
                >
                  {entry.notice}
                </p>
              </div>
            ) : (
              <p className='italic text-gray-400 text-xs'>
                {t("noNoticeProvided")}
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MaintenanceSheet;
